import { ApiService } from "@/services/api/ApiService";
import { PronunciationPair } from "@/types/agent/agent";
import { AgentService } from "./index";

interface PronunciationUpdateAgent {
  id: string;
  name: string;
  elevenlabs_id?: string | null;
  settings?: any | null;
}

export class PronunciationService {
  static async update<T extends PronunciationUpdateAgent>(genie: T, pronunciations: PronunciationPair[]): Promise<T> {
    const validPronunciations = pronunciations.filter((p) => p.word.trim() !== "" && p.pronunciation.trim() !== "");

    const validationErrors = this.validatePronunciations(validPronunciations);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join("\n"));
    }

    const data = await ApiService.callEdge("handle-agent", {
      action: "async-pronunciations",
      agentId: genie.id,
      elevenLabsAgentId: genie.elevenlabs_id,
      agentName: genie.name,
      agentSettings: genie.settings,
      pronunciations: validPronunciations,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to update pronunciations");
    }

    AgentService.invalidateUserAgentsCache();

    const updatedSettings = {
      ...genie.settings,
      pronunciations: validPronunciations,
      pronunciation_dictionary_id: data.data?.pronunciation_dictionary_id,
      pronunciation_dictionary_version_id: data.data?.pronunciation_dictionary_version_id,
    };

    return {
      ...genie,
      settings: updatedSettings,
    };
  }

  static extractJsonFromResponse(content: string): string {
    const jsonMatch = content.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      return jsonMatch[1].trim();
    }
    return content.trim();
  }

  static validatePronunciations(pronunciations: PronunciationPair[]): string[] {
    const errors: string[] = [];

    pronunciations.forEach((pair, index) => {
      if (!pair.word.trim()) {
        errors.push(`Row ${index + 1}: Word cannot be empty`);
      }
      if (!pair.pronunciation.trim()) {
        errors.push(`Row ${index + 1}: Pronunciation cannot be empty`);
      }
      if (pair.word.length > 100) {
        errors.push(`Row ${index + 1}: Word too long (max 100 characters)`);
      }
      if (pair.pronunciation.length > 200) {
        errors.push(`Row ${index + 1}: Pronunciation too long (max 200 characters)`);
      }
    });

    return errors;
  }
}
